import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import AuthService from "../services/auth.service";

const OTP_LENGTH = 4;
const RESEND_COOLDOWN = 30;

/**
 * Prihlásenie do admina telefónnym číslom — pre majiteľov, ktorí sú zároveň
 * zamestnancami a heslo do admina nemajú. Server ich nájde cez rolu
 * v `employee_employer_roles`; kto ju nemá, dostane 404 a odkaz na e-mail.
 */
const PhoneLoginPanel = ({ onBack }) => {
    const navigate = useNavigate();
    const [step, setStep] = useState("phone"); // phone | choose | otp
    const [phone, setPhone] = useState("");
    const [companies, setCompanies] = useState([]);
    const [employeeId, setEmployeeId] = useState(null);
    const [chosenCompany, setChosenCompany] = useState("");
    const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [resendIn, setResendIn] = useState(0);
    const otpRefs = useRef([]);

    useEffect(() => {
        if (resendIn <= 0) return undefined;
        const timer = setInterval(() => setResendIn((prev) => prev - 1), 1000);
        return () => clearInterval(timer);
    }, [resendIn]);

    const apiError = (err, fallback) =>
        (err && err.response && err.response.data && err.response.data.message) || fallback;

    const goToOtp = (id, company) => {
        setEmployeeId(id);
        setChosenCompany(company || "");
        setOtp(Array(OTP_LENGTH).fill(""));
        setResendIn(RESEND_COOLDOWN);
        setStep("otp");
        setTimeout(() => otpRefs.current[0] && otpRefs.current[0].focus(), 60);
    };

    const handlePhoneSubmit = async (e) => {
        if (e) e.preventDefault();
        if (!phone.trim() || busy) return;
        setBusy(true);
        setError(null);
        try {
            const response = await AuthService.phoneLookup(phone.trim());
            if (response.data.status === "choose_company") {
                setCompanies(response.data.companies);
                setStep("choose");
            } else {
                goToOtp(response.data.employeeId, "");
            }
        } catch (err) {
            setError(apiError(err, "Nepodarilo sa overiť číslo. Skúste to znova."));
        } finally {
            setBusy(false);
        }
    };

    const handleChoose = async (company) => {
        if (busy) return;
        setBusy(true);
        setError(null);
        try {
            const response = await AuthService.phoneRequestOtp(phone.trim(), company.employeeId);
            goToOtp(response.data.employeeId, company.company);
        } catch (err) {
            setError(apiError(err, "Nepodarilo sa poslať kód. Skúste to znova."));
        } finally {
            setBusy(false);
        }
    };

    const submitOtp = useCallback(
        async (code) => {
            if (!employeeId || busy) return;
            setBusy(true);
            setError(null);
            try {
                await AuthService.phoneVerifyOtp(employeeId, code);
                navigate("/dashboard");
                window.location.reload();
            } catch (err) {
                setError(apiError(err, "Nesprávny alebo expirovaný kód."));
                setOtp(Array(OTP_LENGTH).fill(""));
                setTimeout(() => otpRefs.current[0] && otpRefs.current[0].focus(), 60);
            } finally {
                setBusy(false);
            }
        },
        [employeeId, busy, navigate]
    );

    const handleOtpChange = (index, value) => {
        const digit = value.replace(/\D/g, "").slice(-1);
        const next = [...otp];
        next[index] = digit;
        setOtp(next);
        if (digit && index < OTP_LENGTH - 1) {
            otpRefs.current[index + 1] && otpRefs.current[index + 1].focus();
        }
        const code = next.join("");
        if (code.length === OTP_LENGTH && !next.includes("")) {
            submitOtp(code);
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            otpRefs.current[index - 1] && otpRefs.current[index - 1].focus();
        }
    };

    const handleResend = async () => {
        if (resendIn > 0 || busy || !employeeId) return;
        setBusy(true);
        setError(null);
        try {
            await AuthService.phoneRequestOtp(phone.trim(), employeeId);
            setResendIn(RESEND_COOLDOWN);
            setOtp(Array(OTP_LENGTH).fill(""));
        } catch (err) {
            setError(apiError(err, "Nepodarilo sa poslať kód."));
        } finally {
            setBusy(false);
        }
    };

    const backToPhone = () => {
        setStep("phone");
        setError(null);
        setCompanies([]);
        setEmployeeId(null);
        setChosenCompany("");
        setOtp(Array(OTP_LENGTH).fill(""));
    };

    return (
        <>
            <button type="button" className="back-to-website-link" onClick={onBack}>
                <ArrowLeft size={16} />
                <span>Prihlásenie e-mailom</span>
            </button>

            {step === "phone" && (
                <>
                    <h2 className="title">Prihlásenie telefónnym číslom</h2>
                    <p className="subtitle">Pošleme vám SMS kód na overenie.</p>
                    <form onSubmit={handlePhoneSubmit}>
                        <div className="form-group">
                            <input
                                type="tel"
                                inputMode="tel"
                                autoComplete="tel"
                                autoFocus
                                className="form-control"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder="0905 123 456"
                            />
                        </div>
                        {error && <div className="alert alert-danger" role="alert">{error}</div>}
                        <div className="form-group">
                            <button className="btn btn-primary btn-block" disabled={busy || !phone.trim()}>
                                <span>{busy ? "Overujem…" : "Poslať kód"}</span>
                            </button>
                        </div>
                    </form>
                </>
            )}

            {step === "choose" && (
                <>
                    <h2 className="title">Vyberte firmu</h2>
                    <p className="subtitle">Toto číslo spravuje viac firiem.</p>
                    {companies.map((company) => (
                        <div className="form-group" key={company.employeeId}>
                            <button
                                type="button"
                                className="btn btn-block"
                                disabled={busy}
                                onClick={() => handleChoose(company)}
                            >
                                {company.company}
                            </button>
                        </div>
                    ))}
                    {error && <div className="alert alert-danger" role="alert">{error}</div>}
                    <button type="button" className="btn-link" onClick={backToPhone}>
                        Zmeniť číslo
                    </button>
                </>
            )}

            {step === "otp" && (
                <>
                    <h2 className="title">Zadajte kód z SMS</h2>
                    <p className="subtitle">
                        Poslali sme ho na {phone}
                        {chosenCompany ? ` pre firmu ${chosenCompany}` : ""}.
                    </p>
                    <div className="otp-row">
                        {otp.map((digit, index) => (
                            <input
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                                ref={(el) => { otpRefs.current[index] = el; }}
                                type="text"
                                inputMode="numeric"
                                autoComplete={index === 0 ? "one-time-code" : "off"}
                                maxLength={1}
                                className="form-control otp-input"
                                value={digit}
                                onChange={(e) => handleOtpChange(index, e.target.value)}
                                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                            />
                        ))}
                    </div>
                    {error && <div className="alert alert-danger" role="alert">{error}</div>}
                    <div className="options">
                        <button type="button" className="btn-link" onClick={backToPhone}>
                            Zmeniť číslo
                        </button>
                        <button
                            type="button"
                            className="btn-link"
                            onClick={handleResend}
                            disabled={resendIn > 0 || busy}
                        >
                            {resendIn > 0 ? `Poslať znova (${resendIn}s)` : "Poslať kód znova"}
                        </button>
                    </div>
                </>
            )}
        </>
    );
};

export default PhoneLoginPanel;
